import { defineStore } from 'pinia'
import { computed, watch, ref } from 'vue'
import { ConfigGetters } from '@/store/configs-store'
import { useStore } from 'vuex'
import { catalogApi, endlessAisleApi } from '@/addons/axios'
import {
  GetAdvancedFiltersData,
  GetBrandCategoriesData,
  GetModelsAttributes,
  GetYmalDataAttributes,
} from '@/api'
import {
  SelectedFiltersE,
  GetModelsFiltersE,
  SelectedFiltersPayloadE,
  StockD,
} from '@/abs-core/pages/catalog/types'

import { handleCatchRequest } from '@/abs-core/pages/catalog/lib/helpers'
import type { LedWallEntitiesT, LedWallEntityD } from '@/modules/fling/index.d'

export const useCatalogStore = defineStore('CatalogStore', () => {
  /**
   * Core
   */
  const store = useStore()

  /**
   * GetStoreConfigDataAttributes['AVAILABLE_BRANDS']
   */
  const availableBrands = computed<string[]>(
    () =>
      store.getters[ConfigGetters.GET_STORE_CONFIGS]?.AVAILABLE_BRANDS ?? [],
  )

  /**
   *
   */
  const categories = ref<GetBrandCategoriesData[]>([])

  /**
   *
   */
  const selectedCategory = ref<string>()

  /**
   *
   */
  const subCategories = computed(
    () =>
      categories.value.find(
        (item) => item?.attributes?.category_code === selectedCategory.value,
      )?.attributes?.subcategories ?? [],
  )

  /**
   *
   */
  const selectedSubCategory = ref<string>()

  /**
   *
   */
  const filters = ref<GetAdvancedFiltersData[]>([])

  /**
   * GET_SELECTED_FILTERS
   */
  const selectedFilters = ref<SelectedFiltersE>({})

  /**
   * SET_SELECTED_FILTERS
   */
  const selectFilters = ({
    context,
    filters,
    value,
  }: SelectedFiltersPayloadE) => {
    if (context === 'filters') {
      if (!selectedFilters.value?.filters) {
        selectedFilters.value.filters = []
      }
      if (filters) {
        selectedFilters.value.filters.push(filters)
      }
    } else {
      selectedFilters.value[context] = value
    }
  }

  /**
   * Reset dependent filters when parent get changed
   */
  watch(selectedCategory, () => (selectedSubCategory.value = undefined))
  watch(selectedSubCategory, () => (selectedFilters.value = {}))

  /**
   * Retrieve categories
   */
  const fetchCategories = (subBrand?: string): Promise<void> =>
    catalogApi
      .apiV1PoswebCategoriesGet(subBrand ?? availableBrands.value?.[0])
      .then(({ data: { data = [] } }) => {
        categories.value = data
      })

  /**
   * GET_TEMP_PRODUCTS
   * SET_TEMP_PRODUCTS
   *
   * WARNING. It is used on "Search By Category", "Guided Serach"
   */
  const productsList = ref<GetModelsAttributes[]>([])

  /**
   * Helper productList for the HeaderSkuInput.
   * Needed a parallel road.
   */
  const productsListHeader = ref<GetModelsAttributes[]>([])

  /**
   *
   * @param code
   */
  const getProduct = (code?: string) =>
    productsList.value.find((product) => product.code === code) ??
    productsListHeader.value.find((product) => product.code === code)

  /**
   * SET_CURRENT_PRODUCT
   * GET_CURRENT_PRODUCT
   */
  const selectedProductId = ref<string>()
  const selectedProductVariant = ref<string>()
  const selectedProduct = computed(() => getProduct(selectedProductId.value))

  /**
   * Retrieve filters
   *
   * GET_FILTERS
   *
   * @param category
   */
  const fetchFilters = ({
    category,
    subCategory,
    subBrand,
  }: {
    category?: string
    subCategory?: string
    subBrand?: string
  }) =>
    catalogApi
      .apiV1PoswebAdvancedFiltersGet(
        subBrand ?? availableBrands.value?.[0],
        subCategory ?? category,
      )
      .then(({ data: { data = [] } }) => (filters.value = data))

  /**
   *
   */
  const productsNotFound = ref(false)

  /**
   * SET_SHOW_LOADER_FULL
   */
  const productsLoading = ref(false)

  /**
   * GET_MODELS
   *
   * TODO: missing total model number (on BE)
   *
   * @Actions
   * @description Action for get models filtered by params
   * @payload GetModelsFiltersE
   * @type Promise<Array<GetModelsAttributes | undefined> | undefined>
   * @return Array<GetModelsAttributes | undefined> | undefined
   * */
  const fetchModelsApi = (payload: GetModelsFiltersE) =>
    catalogApi
      .apiV1PoswebModelsGet(
        payload?.filterSku,
        payload?.filterGetRelated ?? '0',
        payload?.filterPkConsumer,
        payload?.filterBrand,
        payload?.filterCategoria,
        payload?.filterRighe || 20,
        payload?.filterPagina,
        payload?.filterModel,
        payload?.filterModelvariant,
        payload?.filterSizes,
        payload?.filterStock,
        payload?.filterName,
        payload?.filterEan,
        payload?.filterLikeMode,
        payload?.filterRfidStore,
        payload?.filterCodCategoria,
        payload?.filterCodSottocategoria,
        payload?.filterCollection,
        payload?.filterMainline,
        payload?.filterModelB2C,
        payload?.filterNewArrivals,
        payload?.filterOnSale,
        payload?.filterPrice,
        payload?.filterMinprice,
        payload?.filterMaxprice,
        payload?.filterYearSeason,
        payload?.filterStyleFilter,
        payload?.filterBodyLengthFilter,
        payload?.filterColorFilter,
        payload?.filterMaterialFilter,
        payload?.filterTypefilter,
        payload?.filterSleeveLengthFilter,
        payload.filterOccasionFilter,
      )
      .then(
        ({ data: { data = [] } }) =>
          data.map((d) => d.attributes) as GetModelsAttributes[],
      )
      .catch((err) => {
        handleCatchRequest(err)
        return [] as GetModelsAttributes[]
      })

  /**
   * @param filters
   */
  const fetchModels = (
    filters?: GetModelsFiltersE,
  ): Promise<GetModelsAttributes[]> => {
    productsLoading.value = true
    productsNotFound.value = false

    return fetchModelsApi({
      filterBrand:
        availableBrands.value.length > 0
          ? Object.values(availableBrands.value).join(',')
          : '',
      ...filters,
    })
      .then((products) => {
        productsList.value = [...productsList.value, ...products]
        return productsList.value
      })
      .catch(() => {
        productsNotFound.value = true
        return productsList.value
      })
      .finally(() => (productsLoading.value = false))
  }

  /**
   * STOCK_REQUEST
   */
  const fetchStock = (id: string) =>
    endlessAisleApi
      .apiV1StockGet(id, 0)
      .then(
        (r): StockD[] =>
          r.data.data?.map(
            (data) =>
              ({
                ...data.attributes,
                sku: data.id,
              }) as StockD,
          ) ?? [],
      )
      .catch((err) => {
        handleCatchRequest(err)
        return [] as StockD[]
      })

  /**
   * Retrieve categories
   */
  const fetchYMALSKUApi = (
    id: string,
  ): Promise<(GetYmalDataAttributes | undefined)[]> =>
    catalogApi
      .apiV1PoswebYmalGet(availableBrands.value?.[0], id)
      .then(({ data: { data } }) =>
        data?.length ? data?.map((el) => el.attributes) : [],
      )

  /**
   * TODO WARNING:
   * The request should give GetYmalDataAttributes but GetModelsAttributes,
   * very similar, is used in the rest of the code
   * So I force this for now
   *
   * @param id
   */
  const fetchYMALSKU = (id: string): Promise<GetModelsAttributes[]> =>
    // @ts-ignore
    fetchYMALSKUApi(id)

  /**
   * SET_OPENED_PRODUCT_INFO_MODAL
   * SET_OPENED_DRAWER_OR_MODAL
   */
  const selectedModalProduct = ref<string>()
  const selectedVariant = ref<number>()

  const selectedModalProductDetails = computed(() =>
    getProduct(selectedModalProduct.value),
  )

  const catalogOverlayLoading = ref(false)
  const ledWallInfo = ref<{
    list: LedWallEntitiesT
    selectedLed: LedWallEntityD
  }>({
    list: [],
    selectedLed: {} as LedWallEntityD,
  })

  function setLedWallInfo(payload: {
    value: LedWallEntitiesT | LedWallEntityD
    context: string
  }) {
    ;(ledWallInfo.value as Record<string, unknown>)[payload.context] =
      payload.value
  }

  function resetLedWallInfo() {
    ledWallInfo.value = {
      list: [],
      selectedLed: {} as LedWallEntityD,
    }
  }

  return {
    categories,
    selectedCategory,
    subCategories,
    selectedSubCategory,
    filters,
    selectFilters,
    selectedFilters,
    fetchCategories,
    fetchFilters,
    productsList,
    productsListHeader,
    getProduct,
    selectedProductId,
    selectedProductVariant,
    selectedProduct,
    fetchModelsApi,
    fetchModels,
    productsLoading,
    productsNotFound,
    fetchStock,
    fetchYMALSKUApi,
    fetchYMALSKU,
    selectedModalProduct,
    selectedVariant,
    selectedModalProductDetails,
    catalogOverlayLoading,
    ledWallInfo,
    setLedWallInfo,
    resetLedWallInfo,
  }
})
